import { animate, inView, scroll, stagger } from "motion";

// Elements revealed as they scroll into view. Keep in sync with the hidden
// starting state in src/sass/base_layout/motion.sass.
const REVEAL = [
  ".br-section .elementor-widget-heading",
  ".br-section .elementor-widget-text-editor:not(:has(.br-chips))",
  ".br-section .elementor-widget-button",
  ".br-section .elementor-widget-image",
  ".br-section .elementor-widget-shortcode",
  ".br-chips li",
  ".info-card",
  ".blog-card",
  ".logo-card",
  ".br-cta-band",
  ".single-article__header",
  ".single-article__content > *",
  ".footer_main > *",
].join(",");

const EASE = [0.22, 1, 0.36, 1];

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const root = document.documentElement;

window.__brMotion = true;

const show = (elements) => {
  elements.forEach((el) => {
    el.style.opacity = "";
    el.style.transform = "";
  });
};

/**
 * Hero: content staggers in, the photo eases up, the badges pop in and then float.
 */
const animateHero = () => {
  const hero = document.querySelector(".br-hero");
  if (!hero) return;

  const content = hero.querySelectorAll(
    ".elementor-widget:not(.elementor-widget-base-theme-background-blob):not(.br-hero__photo):not(.br-hero__flower):not(.br-hero__badge)"
  );
  animate(content, { opacity: [0, 1], y: [28, 0] }, { duration: 0.9, ease: EASE, delay: stagger(0.09) });

  const photo = hero.querySelector(".br-hero__photo");
  const flower = hero.querySelector(".br-hero__flower");
  const badge = hero.querySelector(".br-hero__badge");

  if (photo) {
    animate(photo, { opacity: [0, 1], y: [40, 0], scale: [0.94, 1] }, { duration: 1.1, ease: EASE, delay: 0.15 });

    // Gentle parallax as the hero scrolls away
    scroll(animate(photo.querySelector("img"), { y: [0, 70] }, { ease: "linear" }), {
      target: hero,
      offset: ["start start", "end start"],
    });
  }

  if (flower) {
    animate(flower, { opacity: [0, 1], scale: [0.4, 1], rotate: [-25, 0] }, { type: "spring", stiffness: 160, damping: 14, delay: 0.7 }).then(() =>
      animate(flower, { y: [0, -10, 0] }, { duration: 5, repeat: Infinity, ease: "easeInOut" })
    );
  }

  if (badge) {
    animate(badge, { opacity: [0, 1], y: [-16, 0] }, { duration: 0.8, ease: EASE, delay: 0.9 }).then(() =>
      animate(badge, { y: [0, 8, 0] }, { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 })
    );
  }
};

/**
 * Scroll reveals. Elements entering the viewport in the same frame are staggered together.
 */
const revealOnScroll = () => {
  const pending = new Set(document.querySelectorAll(REVEAL));
  let queue = [];
  let scheduled = false;

  const enqueue = (el) => {
    if (!pending.delete(el)) return;
    queue.push(el);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  };

  const flush = () => {
    scheduled = false;
    const batch = queue.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top || a.getBoundingClientRect().left - b.getBoundingClientRect().left);
    queue = [];

    batch.forEach((el, i) => {
      // Already scrolled past (e.g. a fast fling or jumping to the end): just show it
      if (el.getBoundingClientRect().bottom < 0) {
        el.style.opacity = "1";
        return;
      }

      const delay = Math.min(i, 8) * 0.07;

      if (el.classList.contains("br-cta-band")) {
        animate(el, { opacity: [0, 1], y: [50, 0], scale: [0.96, 1] }, { duration: 1, ease: EASE, delay });
      } else if (el.matches(".br-chips li")) {
        animate(el, { opacity: [0, 1], scale: [0.8, 1] }, { type: "spring", stiffness: 260, damping: 18, delay: Math.min(i, 12) * 0.04 });
      } else {
        animate(el, { opacity: [0, 1], y: [32, 0] }, { duration: 0.8, ease: EASE, delay });
      }
    });
  };

  inView(REVEAL, enqueue, { margin: "0px 0px -8% 0px" });

  // A fast scroll can carry elements past the viewport between frames, so the
  // observer never sees them; catch anything at or above the fold once scrolling settles.
  let timer;
  window.addEventListener(
    "scroll",
    () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        pending.forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight) enqueue(el);
        });
      }, 120);
    },
    { passive: true }
  );
};

/**
 * The coneflower in the donate panel sways from its stem.
 */
const swayFlowers = () => {
  document.querySelectorAll(".br-cta-band__flower img").forEach((img) => {
    img.style.transformOrigin = "50% 100%";
    animate(img, { rotate: [-2.5, 2.5] }, { duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" });
  });
};

/**
 * Header turns compact and frosted once the page scrolls, with a reading progress bar.
 */
const header = () => {
  const el = document.querySelector("header.wp-block-template-part");
  if (!el) return;

  const update = () => el.classList.toggle("is-scrolled", window.scrollY > 12);
  update();
  window.addEventListener("scroll", update, { passive: true });

  if (reducedMotion) return;

  const bar = document.createElement("div");
  bar.className = "br-progress";
  el.appendChild(bar);
  scroll(animate(bar, { scaleX: [0, 1] }, { ease: "linear" }));
};

/**
 * Menu links slide in one after another when the mobile menu opens.
 */
const mobileMenu = () => {
  const menu = document.querySelector("header .wp-classic-menu-block");
  if (!menu || reducedMotion) return;

  new MutationObserver(() => {
    if (!menu.classList.contains("show")) return;
    animate(menu.querySelectorAll(".menu > li"), { opacity: [0, 1], x: [24, 0] }, { duration: 0.5, ease: EASE, delay: stagger(0.05, { startDelay: 0.1 }) });
  }).observe(menu, { attributes: true, attributeFilter: ["class"] });
};

header();
mobileMenu();

if (reducedMotion || !root.classList.contains("has-motion")) {
  show(document.querySelectorAll(REVEAL));
} else {
  animateHero();
  revealOnScroll();
  swayFlowers();
}
